// Portfolio Administration v1.0 PR 5 — minimal "Add vendor" modal for
// the registry list page. Creates a new Vendor row in Status='active'
// (the stub schema default per PR 1) and redirects to its detail page.
// Vendor Program v3.0 will replace this with the multi-step onboarding
// wizard (per the OOS notes in the spec).

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Label } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError, extractErrorMessage } from "@/api/client";
import type { DedupCheckResultDto } from "@/types/counterparties";
import { useCreateVendor } from "@/hooks/portfolioAdmin/useRegistries";
import { VendorTypeOptGroups } from "./VendorsListFilters";
import { VendorDedupModal } from "./VendorDedupModal";

export interface CreateVendorModalProps {
  open: boolean;
  onClose: () => void;
}

/** A1 — the create endpoint 409s on a Likely/Strong duplicate with the
 *  DedupCheckResultDto riding the envelope's `details` channel (the only
 *  non-2xx payload the shared client surfaces). */
function asDedupResult(err: unknown): DedupCheckResultDto | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const d = err.details as DedupCheckResultDto | undefined;
  if (!d || typeof d !== "object" || !Array.isArray(d.candidates)) return null;
  return d;
}

export function CreateVendorModal({ open, onClose }: CreateVendorModalProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateVendor();
  const [name, setName] = useState("");
  const [vendorType, setVendorType] = useState<string>("Accounting");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dedup, setDedup] = useState<DedupCheckResultDto | null>(null);

  if (!open) return null;

  const submitCreate = async (
    confirmCreateNew = false,
    createNewReason: string | null = null,
  ) => {
    try {
      const result = await create.mutateAsync({
        name: name.trim(),
        vendorType,
        contactName: contactName.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        confirmCreateNew,
        createNewReason,
      });
      toast.success("Vendor created.");
      setDedup(null);
      onClose();
      navigate(`/portfolio-admin/vendors/${result.id}`);
    } catch (err) {
      const dedupResult = asDedupResult(err);
      if (dedupResult) {
        setDedup(dedupResult);
        return;
      }
      toast.error(extractErrorMessage(err));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Vendor name is required.");
      return;
    }
    await submitCreate();
  };

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-onyx/40 p-space-4"
      role="dialog"
      aria-modal="true"
      aria-label="Add vendor"
      data-testid="create-vendor-modal"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white-pure rounded-lg shadow-lg p-space-4 flex flex-col gap-space-3 w-full max-w-md"
      >
        <h2 className="font-display font-bold text-h2 m-0">Add vendor</h2>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="vendor-name-input">Name</Label>
          <Input
            id="vendor-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            data-testid="create-vendor-name"
          />
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="vendor-type-select">Type</Label>
          <select
            id="vendor-type-select"
            className="px-space-2 py-[6px] border border-border-light rounded-sm bg-white-pure text-small"
            value={vendorType}
            onChange={(e) => setVendorType(e.target.value)}
            data-testid="create-vendor-type"
          >
            <VendorTypeOptGroups />
          </select>
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="vendor-contact-input">Contact name</Label>
          <Input
            id="vendor-contact-input"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            data-testid="create-vendor-contact"
          />
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="vendor-email-input">Email</Label>
          <Input
            id="vendor-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="create-vendor-email"
          />
          <p className="text-caption text-gray-mid m-0">
            Needed before a portal invitation can be sent.
          </p>
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="vendor-phone-input">Phone</Label>
          <Input
            id="vendor-phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="create-vendor-phone"
          />
        </div>
        {error ? (
          <p
            className="text-small text-status-error m-0"
            data-testid="create-vendor-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-space-2">
          <Button type="button" variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={create.isPending}
            data-testid="create-vendor-submit"
          >
            {create.isPending ? "Creating…" : "Create vendor"}
          </Button>
        </div>
      </form>
      {dedup ? (
        <VendorDedupModal
          open
          result={dedup}
          pending={create.isPending}
          onUseExisting={(candidate) => {
            setDedup(null);
            onClose();
            navigate(`/portfolio-admin/vendors/${candidate.id}`);
          }}
          onCreateAnyway={(reason) => {
            void submitCreate(true, reason || null);
          }}
          onCancel={() => setDedup(null)}
        />
      ) : null}
    </div>
  );
}
